import userCoreService, {
  AdressData,
  CountryData,
} from 'commons/api/userAccount/userCoreService';
import { isEqual, deepCopy } from 'commons/utils';
import accountValidator from 'commons/api/validators/account/accountValidator';
import pdvService, {
  ValidationStateData,
} from '../../partnerDataValidation/service/pdvService';
import { defineStore } from 'pinia';
import store from 'commons/store/Store';

function resetValidationErrorForField(validation: Validation, field: string) {
  if (validation.errors[field]) {
    delete validation.errors[field];
  }
  return validation;
}

function validateFields(state: State, field: string | string[]) {
  if (!state.validate) {
    return;
  }

  if (Array.isArray(field)) {
    field.forEach((f) => validateFields(state, f));
  } else {
    resetValidationErrorForField(state.validation, field);
    const validationResult = accountValidator.validateByFieldName(
      field,
      state.data,
    );
    state.validation = accountValidator.mergeResults(
      state.validation,
      validationResult,
    );
  }
}

type Validation = {
  errors: Record<string, string>;
  result: boolean;
};

type State = {
  data?: AdressData;
  originalData?: AdressData;
  countries: CountryData[];
  pdvState?: ValidationStateData;
  validate: boolean;
  validation: Validation;
};

export const useUserAddress = defineStore('userAddress', {
  state: (): State => ({
    data: undefined,
    originalData: undefined,
    countries: [],
    pdvState: undefined,
    validate: false,
    validation: {
      errors: {},
      result: true,
    },
  }),

  getters: {
    hasChanged: (state) => !isEqual(state.data, state.originalData),
    isCompany: (state) => typeof state.data?.company === 'string',
  },

  actions: {
    setData(data: State['data']) {
      this.data = data;
      this.originalData = deepCopy(data);
    },
    setCountries(countries: State['countries']) {
      this.countries = countries;
    },
    setPdvState(pdvState: State['pdvState']) {
      this.pdvState = pdvState;
    },
    setValidation(validation: State['validation']) {
      this.validation = validation;
    },
    reset() {
      this.data = deepCopy(this.originalData);
    },
    setCompanyName(company: AdressData['company']) {
      if (!this.data) return;

      this.data.company = company;
      validateFields(this, 'company');
    },
    deleteCompanyName() {
      if (!this.data) return;

      delete this.data.company;
    },
    setLastName(lastName: AdressData['person']['lastName']) {
      if (!this.data) return;

      this.data.person.lastName = lastName;
      validateFields(this, 'lastName');
    },
    setFirstName(firstName: AdressData['person']['firstName']) {
      if (!this.data) return;

      this.data.person.firstName = firstName;
      validateFields(this, 'firstName');
    },
    setCountry(country: CountryData | { code: string; value: string }) {
      if (!this.data) return;

      this.data.country = {
        code: 'countryCode' in country ? country.countryCode : country.code,
        value: 'name' in country ? country.name : country.value,
      };
      validateFields(this, 'country');
    },
    setState(newState: AdressData['state']) {
      if (!this.data) return;

      this.data.state = newState;
      validateFields(this, 'state');
    },
    setStreet(street: AdressData['street']) {
      if (!this.data) return;

      this.data.street = street;
      validateFields(this, 'street');
    },
    setStreet2(street2: AdressData['streetAnnex']) {
      if (!this.data) return;

      this.data.streetAnnex = street2;
      validateFields(this, 'street2');
    },
    setPostalCode(postalCode: AdressData['zipCode']) {
      if (!this.data) return;

      this.data.zipCode = postalCode;
      validateFields(this, 'postalCode');
    },
    setCity(city: AdressData['city']) {
      if (!this.data) return;

      this.data.city = city;
      validateFields(this, 'city');
    },
    async fetchAddressData() {
      await this.fetchPdvState();
      const data = await userCoreService.getAddressData();
      this.setData(data);
    },
    saveAddressData({ forceValidation }: { forceValidation?: boolean } = {}) {
      return new Promise((resolve, reject) => {
        this.validateAll({ forceValidation });
        if (this.data && this.validation.result) {
          userCoreService.updateAddressData(this.data).then(
            async (result) => {
              this.setData(result);
              await this.fetchPdvState();

              store.dispatch('userTaxation/fetchTaxationData');
              store.dispatch('userTrolley/ensureTrolleyStatusIsUpdated');
              resolve(result);
            },
            (error) => {
              reject(error);
            },
          );
        } else {
          reject();
        }
      });
    },
    async fetchCountries() {
      const data = await userCoreService.getCountries();
      this.setCountries(data);
    },
    async fetchPdvState() {
      const data = await pdvService.fetchState();
      this.setPdvState(data);
    },
    validateAll({ forceValidation }: { forceValidation?: boolean }) {
      console.log(!forceValidation && !this.hasChanged);
      if (!forceValidation && !this.hasChanged) {
        return;
      }

      this.validate = true;

      const fieldsToValidate = [
        'firstName',
        'lastName',
        'company',
        'country',
        'city',
        'postalCode',
        'street',
        'street2',
        'state',
      ];

      const val = accountValidator.validateMultipleFieldNames(
        fieldsToValidate,
        this.data,
      );
      this.setValidation(val);
    },
  },
});
